import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

export interface UtSheetAction {
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Background + text classes for the main button, e.g. 'bg-ut-bad text-white'. */
  className?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Small coloured line above the title (match number, date, token count). */
  eyebrow?: React.ReactNode;
  /** Text colour class for the eyebrow, e.g. 'text-ut-bad'. */
  eyebrowClassName?: string;
  title: React.ReactNode;
  children: React.ReactNode;
  cancelLabel?: string;
  main?: UtSheetAction;
  /** While true the sheet can't be dismissed (an action is running). */
  busy?: boolean;
}

/**
 * Game-page sheet from the Game Modals design: a bottom sheet with a drag
 * handle on phones, a centred dialog (max 560px) from `sm` up. Header with a
 * coloured eyebrow, title and ×; scrolling body; footer with cancel and an
 * optional main action.
 */
export const UtSheet: React.FC<Props> = ({
  open,
  onClose,
  eyebrow,
  eyebrowClassName = 'text-ut-acc',
  title,
  children,
  cancelLabel = 'Cancel',
  main,
  busy,
}) => {
  const close = busy ? () => undefined : onClose;
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="ut-root fixed inset-0 z-[60] flex items-end justify-center bg-[rgba(3,5,9,0.66)] pt-10 sm:items-center sm:px-5 sm:py-10"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[20px] border border-ut-line bg-ut-bg2 font-ut-body text-ut-ink shadow-[0_30px_80px_rgba(0,0,0,0.5)] sm:max-h-[90vh] sm:max-w-[560px] sm:rounded-2xl"
      >
        <div className="flex justify-center pt-2 sm:hidden" aria-hidden>
          <span className="h-1 w-10 rounded-sm bg-ut-line" />
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-ut-line px-[18px] pb-3 pt-3.5">
          <div className="flex min-w-0 flex-col gap-[3px]">
            {eyebrow && (
              <span className={clsx('font-ut-display text-xs font-bold uppercase tracking-[0.16em]', eyebrowClassName)}>{eyebrow}</span>
            )}
            <h2 className="font-ut-display text-[26px] font-extrabold uppercase leading-none">{title}</h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] border border-ut-line text-lg hover:bg-ut-card2"
          >
            ×
          </button>
        </div>
        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-4">{children}</div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-ut-line px-[18px] py-3">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="rounded-[10px] border border-ut-line px-4 py-[11px] text-sm font-semibold hover:bg-ut-card2 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          {main && (
            <button
              type="button"
              onClick={main.onClick}
              disabled={main.disabled || busy}
              className={clsx(
                'rounded-[10px] px-[18px] py-[11px] font-ut-display text-[17px] font-extrabold uppercase tracking-[0.06em] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50',
                main.className ?? 'bg-ut-acc text-ut-acc-ink',
              )}
            >
              {main.label}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** Small uppercase section label used inside sheets. */
export const SheetLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span className={clsx('text-[11px] font-semibold uppercase tracking-[0.14em] text-ut-mut', className)}>{children}</span>
);

/** Note row: bold key on the left, text on the right, tinted by tone. */
export const SheetNote: React.FC<{ k: React.ReactNode; tone?: 'acc' | 'bad' | 'plain'; children: React.ReactNode }> = ({ k, tone = 'plain', children }) => (
  <div
    className={clsx(
      'flex gap-2.5 rounded-[10px] border px-3 py-[11px] text-[13px] leading-[1.45]',
      tone === 'acc' && 'border-ut-acc bg-ut-acc-soft',
      tone === 'bad' && 'border-ut-bad bg-ut-bad/[0.08]',
      tone === 'plain' && 'border-ut-line bg-ut-card',
    )}
  >
    <b className={clsx('flex-none', tone === 'acc' && 'text-ut-acc', tone === 'bad' && 'text-ut-bad', tone === 'plain' && 'text-ut-mut')}>{k}</b>
    <span>{children}</span>
  </div>
);
